import axios from "axios";
import { apiUrl, logApiError } from "@/shared/services/config.js";
import { downloadBlob } from "@/shared/utils/misc.js";
import {
  DocuSignEmbeddedSigningResponse,
  PaymentCheckoutRequest,
  PaymentCheckoutResponse,
  PaymentInitiateResponse,
  PaymentPublishableKeyResponse,
  PaymentRequest,
  PaymentStatusResponse,
} from "@/shared/dtos/index.js";

/** @returns {Promise<PaymentPublishableKeyResponse>} */
export const fetchPaymentPublishableKey = async () => {
  try {
    const response = await axios.get(
      apiUrl("/payment/publishable-key"),
      { withCredentials: true },
    );
    return new PaymentPublishableKeyResponse(response.data);
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

/**
 * @param {Partial<PaymentRequest>} payload
 * @returns {Promise<PaymentInitiateResponse>}
 */
export const postPaymentInitiate = async (payload) => {
  try {
    const response = await axios.post(
      apiUrl("/payment/initiate"),
      new PaymentRequest(payload),
      { withCredentials: true },
    );
    return new PaymentInitiateResponse(response.data);
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

/**
 * @param {Partial<PaymentRequest>} payload
 * @returns {Promise<PaymentStatusResponse>}
 */
export const postPaymentConfirm = async (payload) => {
  try {
    const response = await axios.post(
      apiUrl("/payment/confirm"),
      new PaymentRequest(payload),
      { withCredentials: true },
    );
    return new PaymentStatusResponse(response.data);
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

export const postGenerateBinderInvoice = async (submissionId) => {
  try {
    const id = encodeURIComponent(String(submissionId));
    const response = await axios.post(
      apiUrl(`/invoice/${id}/generate-binder-invoice`),
      null,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

const DOCUMENT_KINDS = {
  binder: { path: "binder/download", method: "get", filenamePrefix: "binder" },
  coi: { path: "insured-certificate/download", method: "get", filenamePrefix: "coi" },
  policy: { path: "policy/download", method: "get", filenamePrefix: "policy" },
};

export const downloadDocumentPdf = async (submissionId, kind) => {
  const cfg = DOCUMENT_KINDS[kind];
  if (!cfg) throw new Error(`Unknown document kind: ${kind}`);
  try {
    const url = apiUrl(`/documents/${encodeURIComponent(String(submissionId))}/${cfg.path}`);
    const response = await axios.request({
      url,
      method: cfg.method,
      responseType: "arraybuffer",
      withCredentials: true,
    });
    const blob = new Blob([response.data], { type: "application/pdf" });
    downloadBlob(blob, `${cfg.filenamePrefix}-${submissionId}.pdf`);
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

export const downloadBinderPdf = (submissionId) => downloadDocumentPdf(submissionId, "binder");
export const downloadCOIPdf = (submissionId) => downloadDocumentPdf(submissionId, "coi");
export const downloadPolicyPdf = (submissionId) => downloadDocumentPdf(submissionId, "policy");

// export const fetchDocuSignBinderSigningUrl = async (submissionId) => {
//   try {
//     const returnUrl = getDocuSignDashboardReturnUrl();
//     const response = await axios.get(
//       apiUrl(`/documents/${encodeURIComponent(String(submissionId))}/docusign`),
//       { params: { returnUrl }, withCredentials: true },
//     );
//     const signingUrl = response.data?.signingUrl;
//     if (!signingUrl) throw new Error("Unable to start binder signing. Please try again.");
//     return signingUrl;
//   } catch (error) {
//     logApiError(error);
//     throw error;
//   }
// };

/** @returns {Promise<DocuSignEmbeddedSigningResponse>} */
export const createDocuSignSigningSessionUrl = async (submissionId, returnUrl = null) => {
  try {
    const params = returnUrl == null ? {} : { returnUrl };
    const response = await axios.post(
      apiUrl(`/documents/${encodeURIComponent(String(submissionId))}/docusign`),
      null,
      { params, withCredentials: true },
    );
    const data = new DocuSignEmbeddedSigningResponse(response.data ?? {});
    if (!data.signingUrl && !data.signedCompleted) {
      throw new Error("Unable to start document signing. Please try again.");
    }
    return data;
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

export const putUnderwriterReviewStatus = async (submissionId) => {
  try {
    const id = encodeURIComponent(String(submissionId));
    const response = await axios.put(
      apiUrl(`/insured/${id}/underwriter-review-status`),
      null,
      { withCredentials: true },
    );
    return response.data; // boolean — true = underwriter review required
  } catch (error) {
    logApiError(error);
    throw error;
  }
};
